import { Navigate, useLocation } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';

interface RoleBasedRouterProps {
  isAuthenticated: boolean;
  inOrg: boolean;
  orgId?: string | null;
  children: React.ReactNode;
}

export function RoleBasedRouter({ isAuthenticated, inOrg, children }: RoleBasedRouterProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  // Initialize WebSocket connection for real-time notifications
  // Only connects when authenticated and in an organization
  useWebSocket({ showToasts: true, toastPriorities: ['urgent', 'high'] });

  // Allow public routes (login, onboarding, etc.)
  const publicRoutes = ['/login', '/reset-password', '/customer-invite', '/onboarding'];
  const isCustomerRoute = currentPath.startsWith('/c/');
  const isInvoiceRoute = currentPath.startsWith('/invoices/');
  const isPublicRoute = publicRoutes.includes(currentPath) || isCustomerRoute || isInvoiceRoute;

  if (isPublicRoute) {
    if (isCustomerRoute) {
      return null;
    }
    return <>{children}</>;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Setup (the plan, the import) is drawn on /onboarding itself — a public route above —
  // so the old setup URLs only need sending there.
  if (currentPath === '/organization-setup' || currentPath === '/create-organization') {
    return <Navigate to="/onboarding" replace />;
  }

  // If authenticated but not in organization, redirect to setup
  if (!inOrg) {
    return <Navigate to="/onboarding" replace />;
  }

  // Onboarding check is handled by AppContent (with localStorage cache protection)
  // so we don't duplicate it here — duplicating caused false redirects to setup
  // when the useOrganization query failed (e.g., expired session)
  return <>{children}</>;
}
